import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { isHandicap, isOU, sortOdds } from '@/core/lib/oddsHelper';
import type { IOdds } from '@/interface/IOdds';
import type { IBetBuilderMarket } from '@/interface/betBuilder';
import type { MarketType, SportType } from '@/interface/enum';

export function buildBetBuilderPointOrder(oddsList: IOdds[], sportType: SportType): Map<MarketType, number[]> {
    const orderableOdds = oddsList.filter(odds => isHandicap(odds.marketType) || isOU(odds.marketType));
    const grouped = groupBy(sortOdds(orderableOdds, sportType), odds => odds.marketType);

    const result = new Map<MarketType, number[]>();
    Object.values(grouped).forEach((list) => {
        result.set(list[0].marketType, list.map(odds => odds.point));
    });

    return result;
}

export function sortBetBuilderMarketsByPointOrder(
    markets: IBetBuilderMarket[],
    pointOrder: number[] | undefined,
    marketType: MarketType | undefined,
): IBetBuilderMarket[] {
    if (!pointOrder || pointOrder.length === 0 || marketType === undefined) {
        return markets;
    }

    const invertSign = isHandicap(marketType);
    // Keep the first occurrence of each point: the normal match list may repeat a
    // point (e.g. the same line across market groups), and the earliest one
    // reflects its real position in the normal match order.
    const rankByPoint = new Map<number, number>();
    pointOrder.forEach((point, index) => {
        if (!rankByPoint.has(point)) {
            rankByPoint.set(point, index);
        }
    });

    const matched: { market: IBetBuilderMarket, rank: number }[] = [];
    const unmatched: IBetBuilderMarket[] = [];

    markets.forEach((market) => {
        const lookupPoint = invertSign ? -market.point : market.point;
        const rank = rankByPoint.get(lookupPoint);
        if (rank === undefined) {
            unmatched.push(market);
        } else {
            matched.push({ market, rank });
        }
    });

    const sortedMatched = orderBy(matched, entry => entry.rank).map(entry => entry.market);

    return [...sortedMatched, ...unmatched];
}

export function sortBetBuilderMarketsByDisplayPriority(markets: IBetBuilderMarket[]): IBetBuilderMarket[] {
    return orderBy(markets, ['displayPriority', 'point'], ['desc', 'asc']);
}
